/** @jsx jsx */
import React, { useMemo } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { jsx } from '@emotion/react'
import gql from 'graphql-tag'
import get from 'lodash/get'

import TrackPageview from '../components/tracking/TrackPageview'
import A from '../components/typography/A'
import DisplayH1 from '../components/typography/DisplayH1'
import H1 from '../components/typography/H1'
// import LogoArtlingProjectsWhiteSvg from '../static/LogoArtlingProjectsWhite.svg'
import LogoArtlingProjectsWhiteSvg from '../static/LogoArtlingProjectsWhiteBeta.svg'
import LoginForm from '../components/hookforms/forms/LoginForm'
import { useLoginViewQuery } from '../generated/graphql'
import theme, { mq } from '../theme'

const query = gql`
  query LoginView {
    staticImage(identifier: "login-bg") {
      id
      image
      altText
    }
  }
`

const LoginView: React.FC<RouteComponentProps> = ({ history }) => {
  const { data: qData, loading: qLoading } = useLoginViewQuery()

  const bgUrl = useMemo(
    () =>
      `${process.env.RAZZLE_IMAGEKIT_PATH}${get(
        qData,
        'staticImage.image',
        ''
      )}`,
    [qData]
  )

  if (!!qLoading) {
    return null
  }

  return (
    <main css={{ width: '100%', height: '100%' }}>
      <TrackPageview />
      <div
        // for mobile, this div just sets a background

        // for desktop, this div becomes a flex container and creates the
        // two-column layout (and no longer shows the background)
        css={[
          {
            height: '100%',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            overflowY: 'scroll',
            // minHeight: '830px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          },
          mq('', 'maxDesktop', {
            background: `
            linear-gradient(
              rgba(0, 0, 0, 0.2), 
              rgba(0, 0, 0, 0.2)
            ),
            url(${bgUrl}?tr=bl-20)`,
          }),
          mq('', 'minDesktop', {
            flexDirection: 'row',
          }),
        ]}
      >
        <div
          css={[
            mq('', 'maxDesktop', {
              padding: '0em 1em 0px 1em',
              flex: '0 0 auto',
            }),
            mq('', 'minDesktop', {
              flex: '1 1 auto',
              background: `
              linear-gradient(
                rgba(0, 0, 0, 0.1), 
                rgba(0, 0, 0, 0.1)
              ),
              url(${bgUrl})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              padding: '6em',
              display: 'flex',
              flexDirection: 'column',
            }),
          ]}
        >
          <img
            src={LogoArtlingProjectsWhiteSvg}
            css={[
              mq('mt2', 'maxDesktop'),
              mq('', 'maxDesktop', { width: '100px' }),
              mq('center', 'maxDesktop'),
              mq('', 'minDesktop', { width: '150px' }),
            ]}
          />
          <div
            css={[
              mq('', 'minDesktop', {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                paddingBottom: '200px',
              }),
            ]}
          >
            <DisplayH1
              css={[
                mq('text-white'),
                mq('text-center', 'maxDesktop'),
                mq('center', 'maxDesktop'),
                mq('', 'maxDesktop', {
                  fontSize: '42px',
                  lineHeight: '60px',
                }),
                mq('', 'maxDesktop', {
                  maxWidth: '280px',
                  fontSize: '18px',
                  letterSpacing: '0.45px',
                  lineHeight: '20px',
                }),
              ]}
            >
              An industry solution for sourcing Art & Design. Specify pieces for
              your projects and spaces, seamlessly.
            </DisplayH1>

            <A
              href="https://theartling.com/en/advisory/interior-designers-architects/"
              css={[
                mq('d-none', 'maxDesktop'),
                mq('text-left'),
                mq('d-block'),
                mq('mt2'),
                mq('text-uppercase'),
                mq('text-white'),
              ]}
            >
              Find Out More
            </A>
          </div>
        </div>

        <div
          css={[
            mq('', 'maxDesktop', {
              padding: '0px 3em 0em 3em',
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }),
            mq('', 'minDesktop', {
              flex: '1 1 70%',
              padding: '0px 6em',
              background: theme.colors.grayBg1,
              minWidth: '400px',
            }),
          ]}
        >
          <div
            css={[
              mq('center'),
              { maxWidth: '400px' },
              mq('', 'minDesktop', {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                paddingBottom: '100px',
              }),
            ]}
          >
            <H1 css={[mq('text-white', 'maxDesktop'), mq('text-left')]}>
              Log In
            </H1>
            <LoginForm
              buttonCss={[
                mq('', 'maxDesktop', { marginTop: '6em', width: '100%' }),
              ]}
            />

            <A
              href="https://theartling.com/en/advisory/interior-designers-architects/"
              css={[
                mq('d-none', 'minDesktop'),
                mq('text-center'),
                mq('d-block'),
                mq('mt2'),
                mq('text-uppercase'),
                mq('text-white'),
              ]}
            >
              Find Out More
            </A>
          </div>
        </div>
      </div>
    </main>
  )
}

export default LoginView
